import type { NavigationGuard } from 'vue-router'

import { ADMIN_HOME_ROUTE_NAME, HOME_ROUTE_NAME, LOGIN_ROUTE_NAME } from '../routeNames'

import { useRbac } from '@/features/account'
import { useSession } from '@/features/auth'

/** The reads authGuard needs, snapshotted per navigation (injected as fakes in unit tests). */
export interface AuthGuardSnapshot {
  isAuthenticated: boolean
  isSysAdmin: boolean
  rememberTarget: (name: string | null) => void
}
// Live reads — 06's token seam `useSession` (NEVER useAccountSession) + 07's `useRbac` for the
// authed landing choice only (ledger A4: isSysAdmin never from a session composable). Both are
// synchronous store reads; isSysAdmin is falsy pre-`ready` → a cold boot never false-redirects.
const readLive = (): AuthGuardSnapshot => {
  const session = useSession()
  return {
    isAuthenticated: session.isAuthenticated.value,
    isSysAdmin: useRbac().isSysAdmin.value,
    rememberTarget: session.rememberTarget,
  }
}
// Token gate (legacy :231–240; golden rows 28/29): redirect + remember only — NO toasts, NO
// HTTP, no store write beyond rememberTarget. Return-value style, never next() (contract §11).
export const createAuthGuard =
  (read: () => AuthGuardSnapshot = readLive): NavigationGuard =>
  (to) => {
    const session = read()
    if (to.meta.guestOnly) {
      if (!session.isAuthenticated) return true
      return { name: session.isSysAdmin ? ADMIN_HOME_ROUTE_NAME : HOME_ROUTE_NAME }
    }
    if (to.meta.requiresAuth === false || session.isAuthenticated) return true
    const handoff = to.query.handoff
    if (typeof handoff === 'string' && handoff) {
      return { name: LOGIN_ROUTE_NAME, query: { handoff } }
    }
    session.rememberTarget(typeof to.name === 'string' ? to.name : null)
    return { name: LOGIN_ROUTE_NAME, query: { next: to.fullPath } }
  }
export const authGuard: NavigationGuard = createAuthGuard()
